import api from '@/services/apiService';
import { assertApiSuccess, type ApiResponse } from '@/types/api';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';

const BASE = '/whatsapp/community';

export type WhatsappSessionStatus = 'pending' | 'active' | 'disconnected' | string;

export type WhatsappMessageStatus =
  | 'pending'
  | 'sent'
  | 'delivered'
  | 'read'
  | 'failed'
  | 'cancelled'
  | string;

export interface WhatsappCommunityStatus {
  enabled: boolean;
  community_id: number;
  checked_at: string;
}

export interface WhatsappCommunitySession {
  session_id: string;
  arena_id: number;
  community_id?: number | null;
  qr_code: string | null;
  device_name: string;
  phone_number: string | null;
  status: WhatsappSessionStatus;
  last_seen: string | null;
  created_at: string;
  updated_at: string;
}

export interface WhatsappCommunityCreateSession {
  qrCode: string;
  sessionId: string;
  status?: string;
}

export interface WhatsappCommunityMessage {
  id: number;
  session_id: string;
  message_id: string;
  to_number: string;
  message_type: string;
  message_content: string;
  media_url: string | null;
  status: WhatsappMessageStatus;
  error_message: string | null;
  sent_at: string | null;
  delivered_at: string | null;
  read_at: string | null;
  failed_at: string | null;
  retry_count: number;
  created_at: string;
  updated_at: string;
}

export interface WhatsappCommunityPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface WhatsappCommunityMessagesData {
  messages: WhatsappCommunityMessage[];
  pagination: WhatsappCommunityPagination;
}

export interface ListMessagesParams {
  page?: number;
  limit?: number;
  status?: string;
}

function mapError(error: unknown, fallback: string): Error {
  // assertApiSuccess throws ApiError with a human-readable message already
  if (error instanceof Error && error.message.trim()) {
    return error;
  }
  return new Error(getApiErrorMessage(error, fallback));
}

export async function getStatus(): Promise<WhatsappCommunityStatus> {
  try {
    const res = await api.get<ApiResponse<WhatsappCommunityStatus>>(`${BASE}/status`);
    assertApiSuccess(res.data);
    return res.data.data;
  } catch (error) {
    throw mapError(error, 'Gagal mengambil status WhatsApp');
  }
}

export async function listSessions(): Promise<WhatsappCommunitySession[]> {
  try {
    const res = await api.get<ApiResponse<WhatsappCommunitySession[]>>(`${BASE}/sessions`);
    assertApiSuccess(res.data);
    return Array.isArray(res.data.data) ? res.data.data : [];
  } catch (error) {
    throw mapError(error, 'Gagal mengambil sesi WhatsApp');
  }
}

export async function createSession(
  deviceName?: string
): Promise<WhatsappCommunityCreateSession> {
  try {
    const body =
      deviceName && deviceName.trim() !== '' ? { device_name: deviceName.trim() } : {};
    const res = await api.post<ApiResponse<WhatsappCommunityCreateSession>>(
      `${BASE}/sessions`,
      body
    );
    assertApiSuccess(res.data);
    return res.data.data;
  } catch (error) {
    throw mapError(error, 'Gagal membuat sesi WhatsApp');
  }
}

export async function deleteSession(sessionId: string): Promise<void> {
  try {
    const res = await api.delete<ApiResponse<null>>(`${BASE}/sessions/${sessionId}`);
    assertApiSuccess(res.data);
  } catch (error) {
    throw mapError(error, 'Gagal memutus sesi WhatsApp');
  }
}

export async function listMessages(
  params: ListMessagesParams = {}
): Promise<WhatsappCommunityMessagesData> {
  try {
    const res = await api.get<ApiResponse<WhatsappCommunityMessagesData>>(`${BASE}/messages`, {
      params: {
        page: params.page ?? 1,
        limit: params.limit ?? 20,
        ...(params.status ? { status: params.status } : {}),
      },
    });
    assertApiSuccess(res.data);
    const data = res.data.data;
    return {
      messages: Array.isArray(data?.messages) ? data.messages : [],
      pagination: data?.pagination ?? {
        page: params.page ?? 1,
        limit: params.limit ?? 20,
        total: 0,
        totalPages: 0,
      },
    };
  } catch (error) {
    throw mapError(error, 'Gagal mengambil log pesan WhatsApp');
  }
}

export function countActiveSessions(sessions: WhatsappCommunitySession[]): number {
  return sessions.filter((s) => s.status === 'active').length;
}

export function toQrImageSrc(qr: string | null | undefined): string | undefined {
  if (!qr) return undefined;
  if (qr.startsWith('data:') || qr.startsWith('http://') || qr.startsWith('https://')) {
    return qr;
  }
  return `data:image/png;base64,${qr}`;
}

const whatsappCommunityService = {
  getStatus,
  listSessions,
  createSession,
  deleteSession,
  listMessages,
  countActiveSessions,
  toQrImageSrc,
};

export default whatsappCommunityService;
